import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const STATUS_CONFIG = {
  '알림톡성공': { color: '#0F6E56', bg: '#E1F5EE', label: '알림톡 성공' },
  '문자대체': { color: '#854F0B', bg: '#FAEEDA', label: '문자 대체 발송' },
  '발송실패': { color: '#A32D2D', bg: '#FCEBEB', label: '발송 실패' },
  '확인실패': { color: '#666', bg: '#f0f0f0', label: '결과 확인 실패(발송은 됐을 수 있음)' },
};

const FILTERS = [
  { key: 'all', label: '전체' },
  { key: '알림톡성공', label: '알림톡 성공' },
  { key: '문자대체', label: '문자 대체' },
  { key: '발송실패', label: '발송 실패' },
  { key: '확인실패', label: '확인 실패' },
];

export default function AlimtalkLog() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [recheckingId, setRecheckingId] = useState(null);
  const [searchOrderNumber, setSearchOrderNumber] = useState('');
  const [running, setRunning] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/alimtalk', { params: { status, page } });
      setItems(r.data.items);
      setTotalPages(r.data.totalPages);
      setTotal(r.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [status, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRetry = async (id) => {
    setRetryingId(id);
    try {
      await api.post(`/alimtalk/${id}/retry`);
      alert('재전송 요청을 보냈습니다. 잠시 후 새로고침하여 결과를 확인해주세요.');
      fetchData();
    } catch (err) {
      alert('재전송 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setRetryingId(null);
    }
  };
  // [기능 추가 - 2026-09-13] 알리고에서는 실제로 발송됐는데, 우리 시스템(예: 서버
  // 재시작 타이밍 등)이 내역을 놓친 경우를 위한 기능. 이미 내역이 있으면 재발송하지 않고
  // 그대로 표시만 하고, 없으면 그 자리에서 알림톡을 실행해 새로 기록한다.
  const handleRunByOrderNumber = async () => {
    if (!searchOrderNumber.trim()) return alert('주문번호를 입력해주세요');
    setRunning(true);
    try {
      const r = await api.post('/alimtalk/run-by-order-number', { orderNumber: searchOrderNumber.trim() });
      alert(r.data.message);
      fetchData();
    } catch (err) {
      alert('실행 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setRunning(false);
    }
  };
  // [기능 추가 - 2026-09-11] "확인실패" 건은, 추가 발송(중복 문자 위험) 없이 먼저
  // 이미 보낸 알림톡의 실제 결과만 다시 확인한다. 그래도 해결 안 되면 재전송 버튼을 쓴다.
  const handleRecheck = async (id) => {
    setRecheckingId(id);
    try {
      const r = await api.post(`/alimtalk/${id}/recheck`);
      alert(r.data.message);
      if (r.data.updated) fetchData();
    } catch (err) {
      alert('확인 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setRecheckingId(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const date = new Date(d);
    const pad = n => String(n).padStart(2, '0');
    return `${date.getFullYear()}.${pad(date.getMonth()+1)}.${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>알림톡 내역</h2>
        <span style={{ fontSize: '13px', color: '#888' }}>총 {total}건</span>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
        <input value={searchOrderNumber} onChange={e => setSearchOrderNumber(e.target.value)}
          placeholder="주문번호로 재실행 (실제 발송여부 확인/등록)"
          style={{ fontSize: '12px', padding: '6px 10px', borderRadius: '8px', border: '0.5px solid #ddd', width: '260px' }} />
        <button onClick={handleRunByOrderNumber} disabled={running}
          style={{ fontSize: '12px', padding: '6px 14px', borderRadius: '8px', border: '0.5px solid #0064FF', background: running ? '#f0f0f0' : '#E6EEFF', color: running ? '#aaa' : '#0040B0', cursor: running ? 'not-allowed' : 'pointer', fontWeight: '500' }}>
          {running ? '확인 중...' : '실행'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => { setStatus(f.key); setPage(1); }}
            style={{
              fontSize: '12px', padding: '6px 14px', borderRadius: '8px', cursor: 'pointer',
              border: `0.5px solid ${status === f.key ? '#0064FF' : '#ddd'}`,
              background: status === f.key ? '#E6EEFF' : '#fff',
              color: status === f.key ? '#0040B0' : '#555', fontWeight: status === f.key ? '500' : '400',
            }}>
            {f.label}
          </button>
        ))}
      </div>

      <div style={{ background: '#fff', borderRadius: '10px', border: '0.5px solid #e8e8e8', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#fafafa', borderBottom: '0.5px solid #e8e8e8' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', color: '#888' }}>발송시각</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', color: '#888' }}>주문번호</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', color: '#888' }}>수령인</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', color: '#888' }}>상품명</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', color: '#888' }}>템플릿</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', color: '#888' }}>상태</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', color: '#888' }}>사유</th>
              <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: '12px', color: '#888' }}>작업</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: '30px', textAlign: 'center', color: '#aaa', fontSize: '13px' }}>불러오는 중...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: '30px', textAlign: 'center', color: '#aaa', fontSize: '13px' }}>내역이 없습니다</td></tr>
            ) : items.map(item => {
              const cfg = STATUS_CONFIG[item.status] || { color: '#888', bg: '#f5f5f5', label: item.status };
              return (
                <tr key={item.id} style={{ borderBottom: '0.5px solid #f0f0f0' }}>
                  <td style={{ padding: '10px 14px', fontSize: '12px', color: '#666' }}>{formatDate(item.sent_at)}</td>
                  <td style={{ padding: '10px 14px', fontSize: '12px' }}>{item.order_number || '-'}</td>
                  <td style={{ padding: '10px 14px', fontSize: '12px' }}>{item.receiver_name || '-'}</td>
                  <td style={{ padding: '10px 14px', fontSize: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.product_name || '-'}</td>
                  <td style={{ padding: '10px 14px', fontSize: '12px', color: '#888' }}>{item.template_code}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ fontSize: '11px', padding: '3px 9px', borderRadius: '6px', background: cfg.bg, color: cfg.color, fontWeight: '500' }}>{cfg.label}</span>
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: '12px', color: '#A32D2D', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.fail_reason}>{item.fail_reason || '-'}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                    {item.status === '확인실패' && (
                      <button onClick={() => handleRecheck(item.id)} disabled={recheckingId === item.id}
                        style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #888', background: recheckingId === item.id ? '#f0f0f0' : '#f5f5f5', color: recheckingId === item.id ? '#aaa' : '#555', cursor: recheckingId === item.id ? 'not-allowed' : 'pointer', marginRight: '4px' }}>
                        {recheckingId === item.id ? '확인 중...' : '발송여부 확인'}
                      </button>
                    )}
                    {(item.status === '발송실패' || item.status === '확인실패') && (
                      <button onClick={() => handleRetry(item.id)} disabled={retryingId === item.id}
                        style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #0064FF', background: retryingId === item.id ? '#f0f0f0' : '#E6EEFF', color: retryingId === item.id ? '#aaa' : '#0040B0', cursor: retryingId === item.id ? 'not-allowed' : 'pointer' }}>
                        {retryingId === item.id ? '재전송 중...' : '재전송'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '16px' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ fontSize: '12px', padding: '6px 12px', borderRadius: '6px', border: '0.5px solid #ddd', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer' }}>이전</button>
          <span style={{ fontSize: '12px', color: '#888', padding: '6px 8px' }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ fontSize: '12px', padding: '6px 12px', borderRadius: '6px', border: '0.5px solid #ddd', background: '#fff', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>다음</button>
        </div>
      )}
    </div>
  );
}
